import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { Store } from '@ngrx/store';
import { CuentaBeneficiario } from 'src/app/dashboard/interfaces/cuenta-beneficiario.interface';
import { AccountRecipientService } from 'src/app/dashboard/services/account-recipient.service';
import { ParameterService } from 'src/app/dashboard/services/parameter.service';
import { PAGO_MOVIL_MONTO_MAX_DEFAULT } from 'src/app/shared/enums/parameters.enum';
import AddAccountComponent from '../../../account-recipient/components/add-account/add-account.component';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { esBeneficiarioBancoVenezolano } from 'src/app/shared/utils/saldo-ves';

@Component({
  selector: 'app-withdraw-accounts-step-ves',
  templateUrl: './withdraw-accounts-step-ves.component.html',
  styleUrls: ['./withdraw-accounts-step-ves.component.sass']
})
export class WithdrawAccountsStepVesComponent implements OnInit {
  public subscription: Subscription = new Subscription();
  public user: any;
  public searchTxt: string = '';
  // Monto (Bs) a retirar, viene del paso calculadora. El límite de Pago Móvil se compara contra este monto (bruto).
  @Input() public montoRetiro: number = 0;
  // Límite máximo (Bs) para Pago Móvil, configurable desde admin (PAGO_MOVIL_MONTO_MAX).
  public pagoMovilMontoMax: number = PAGO_MOVIL_MONTO_MAX_DEFAULT;

  constructor(
    private dialog: MatDialog,
    private accountRecipientService: AccountRecipientService,
    private store: Store<AppState>,
    private parameterService: ParameterService,
    private router: Router,
  ){}

  ngOnInit(): void {
    this.subscription.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.user = user;
        this.getBeneficiarios();
      })
    );
    this.cargarPagoMovilMontoMax();
  }

  async cargarPagoMovilMontoMax(): Promise<void> {
    this.pagoMovilMontoMax = await this.parameterService.getPagoMovilMontoMax();
  }

  /** El retiro por Pago Móvil no se permite si el monto (Bs) supera el límite. */
  get isPagoMovilBlockedByAmount(): boolean {
    return this.montoRetiro > this.pagoMovilMontoMax;
  }

  /** Un beneficiario de Pago Móvil no se puede elegir si el monto supera el límite. */
  isBeneficiaryPagoMovilBlocked(account: CuentaBeneficiario): boolean {
    return account?.movilPay === 1 && this.isPagoMovilBlockedByAmount;
  }

  @Output()
  public changeStep = new EventEmitter<number>();

  @Output()
  public beneficiarioOutput = new EventEmitter<CuentaBeneficiario>();

  public accountSelected: CuentaBeneficiario | undefined = undefined;
  public accountsList: CuentaBeneficiario[] = [];
  public accountsListFiltered: CuentaBeneficiario[] = [];

  openCreateDialog(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AddAccountComponent, {
      data: {
        type: 'CREATE',
        isVeUser: true,
        // Monto en Bs del retiro: bloquea crear beneficiario Pago Móvil si supera el límite
        montoBs: this.montoRetiro,
      },
      panelClass: 'aguacate-dialog',
      width: isMobile ? '95vw' : '560px',
      maxWidth: isMobile ? '95vw' : '560px',
      maxHeight: '90vh'
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result === true) {
        await this.getBeneficiarios();
      }
    });
  }

  elegir(account: CuentaBeneficiario): void {
    // Al intentar seleccionar un Pago Móvil con monto sobre el límite: avisar y no seleccionar
    if (this.isBeneficiaryPagoMovilBlocked(account)) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'warning',
        title: 'Pago Móvil no disponible',
        text: `Pago Móvil no está disponible para montos superiores a ${this.pagoMovilMontoMax.toLocaleString(
          'es-VE',
        )} Bs. Selecciona un beneficiario de otro tipo.`,
        confirmButtonText: 'Entendido',
      });
      return;
    }
    if (this.accountSelected === account) {
      this.accountSelected = undefined;
      return;
    }
    this.accountSelected = account;
  }

  emitChangeStep(): void {
    // Defensa: no avanzar con un beneficiario Pago Móvil bloqueado por monto
    if (
      this.accountSelected &&
      this.isBeneficiaryPagoMovilBlocked(this.accountSelected)
    ) {
      return;
    }
    this.changeStep.emit(3);
    this.beneficiarioOutput.emit(this.accountSelected);
  }

  /** Ya se consultó la libreta (para no mostrar el aviso de vacío mientras carga). */
  public cargado = false;

  /**
   * Solo beneficiarios de bancos venezolanos: el retiro VES paga en Venezuela
   * y Nest rechaza otro país (`banco.codigoPais !== 'VE'` → 400). El chileno
   * también tiene beneficiarios de Colombia/Perú/Chile para sus envíos CLP.
   */
  async getBeneficiarios(): Promise<void> {
    if (!this.user?.usuarioId) return;
    const todos = await this.accountRecipientService.getByUser(this.user.usuarioId);
    this.accountsList = (todos || []).filter((c) => esBeneficiarioBancoVenezolano(c));
    this.cargado = true;
    // El elegido se busca por id en la lista nueva; si ya no está, se descarta.
    if (this.accountSelected) {
      const id = this.accountSelected.cuentaBeneficiarioID;
      this.accountSelected = this.accountsList.find((c) => c.cuentaBeneficiarioID === id);
    }
    this.buscar();
  }

  /** Libreta de beneficiarios, para registrar uno de un banco venezolano. */
  irALibreta(): void {
    this.router.navigate(['/wallet/client/recipients']);
  }

  buscar(event?: KeyboardEvent): void {
    const searchWord = this.searchTxt.toLowerCase();
    if (searchWord === '') {
      this.accountsListFiltered = this.accountsList;
      return;
    }
    this.accountsListFiltered = this.accountsList.filter(cuenta => {
      const nombre = `${cuenta.nombre} ${cuenta.apellidos || ''}`.toLowerCase();
      return nombre.includes(searchWord);
    });
  }
}
